import { useState } from 'react';

import { format, parseISO } from 'date-fns';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { IncidentStatus, IncidentSummary } from '@/types/service/serviceHealth';

import { Button } from '@/components/ui/button';

import IncidentCard from './IncidentCard';

interface IncidentTimelineProps {
  incidents: IncidentSummary[];
}

interface GroupedIncidents {
  [date: string]: IncidentSummary[];
}

const IncidentTimeline = ({ incidents }: IncidentTimelineProps) => {
  const [showAll, setShowAll] = useState(false);

  // Filter out ongoing incidents (they're shown at the top)
  const resolvedIncidents = incidents.filter(
    (incident) => incident.status === IncidentStatus.RESOLVED,
  );

  if (resolvedIncidents.length === 0) {
    return (
      <div className='text-center py-8 text-muted-foreground'>
        <p>No incidents reported in the past 90 days.</p>
      </div>
    );
  }

  // Group incidents by date
  const groupedIncidents: GroupedIncidents = resolvedIncidents.reduce((acc, incident) => {
    try {
      const date = format(parseISO(incident.start_time), 'MMMM dd, yyyy');
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(incident);
      return acc;
    } catch {
      return acc;
    }
  }, {} as GroupedIncidents);

  const dates = Object.keys(groupedIncidents).sort(
    (a, b) => parseISO(b).getTime() - parseISO(a).getTime(),
  );

  // Show only first 5 dates by default
  const displayDates = showAll ? dates : dates.slice(0, 5);
  const hasMore = dates.length > 5;

  return (
    <div className='space-y-6'>
      {displayDates.map((date) => (
        <div key={date} className='space-y-3'>
          <h3 className='text-sm font-semibold text-muted-foreground'>{date}</h3>
          <div className='space-y-3'>
            {groupedIncidents[date].map((incident) => (
              <IncidentCard key={incident.id} incident={incident} showFullDetails={true} />
            ))}
          </div>
        </div>
      ))}

      {hasMore && (
        <div className='flex justify-center pt-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setShowAll(!showAll)}
            className='gap-2'
          >
            {showAll ? (
              <>
                Show less
                <ChevronUp className='h-4 w-4' />
              </>
            ) : (
              <>
                Show more history
                <ChevronDown className='h-4 w-4' />
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default IncidentTimeline;
